import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Drawer,
  List,
  Divider,
  Badge,
  Fab,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Explore as ExploreIcon,
  Height as HeightIcon,
  Speed as SpeedIcon,
  ChevronRight as ChevronRightIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';

import SensorMap from '../components/SensorMap';
import AlarmPanel from '../components/AlarmPanel';
import {
  sensorsService,
  displacementsService,
  alarmsService,
  createWebSocket,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PANEL_WIDTH = 360;

function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  const [sensors, setSensors] = useState([]);
  const [displacements, setDisplacements] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [summary, setSummary] = useState(null);
  const [, setLoading] = useState(true);
  const [showArrows, setShowArrows] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelTab, setPanelTab] = useState('displacements');

  // Carica dati iniziali
  const loadData = useCallback(async () => {
    try {
      const [sensorsData, displData, alarmsData, summaryData] = await Promise.all([
        sensorsService.getAll(true),
        displacementsService.getAllLatest(),
        alarmsService.getActive(),
        sensorsService.getSummary(),
      ]);

      setSensors(sensorsData);
      setDisplacements(displData.displacements || []);
      setAlarms(alarmsData.alarms || []);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    // Setup WebSocket per aggiornamenti real-time
    const ws = createWebSocket(
      (data) => {
        if (data.type === 'position') {
          setSensors((prev) =>
            prev.map((s) =>
              s.id === data.sensor_id
                ? {
                    ...s,
                    current_latitude: data.data.latitude,
                    current_longitude: data.data.longitude,
                    current_altitude: data.data.altitude,
                    last_seen: data.timestamp,
                  }
                : s
            )
          );
        } else if (data.type === 'status') {
          setSensors((prev) =>
            prev.map((s) =>
              s.id === data.sensor_id ? { ...s, status: data.status } : s
            )
          );
        } else if (data.type === 'alarm') {
          setAlarms((prev) => [data.data, ...prev]);
        } else if (data.type === 'displacement') {
          setDisplacements((prev) => {
            const exists = prev.some((d) => d.sensor_id === data.sensor_id);
            if (exists) {
              return prev.map((d) =>
                d.sensor_id === data.sensor_id ? { sensor_id: data.sensor_id, ...data.data } : d
              );
            } else {
              return [...prev, { sensor_id: data.sensor_id, ...data.data }];
            }
          });
        }
      },
      () => setWsConnected(true),
      () => setWsConnected(false)
    );

    const interval = setInterval(loadData, 30000);

    return () => {
      ws.close();
      clearInterval(interval);
    };
  }, [loadData]);

  const handleAcknowledge = async (alarmId) => {
    try {
      await alarmsService.acknowledge(alarmId, user?.username || 'unknown');
      loadData();
    } catch (error) {
      console.error('Error acknowledging alarm:', error);
    }
  };

  const handleResolve = async (alarmId) => {
    try {
      await alarmsService.resolve(alarmId);
      loadData();
    } catch (error) {
      console.error('Error resolving alarm:', error);
    }
  };

  const formatMM = (v) => (v !== undefined && v !== null ? `${(v * 1000).toFixed(1)}` : '--');

  return (
    <Box sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* Mappa a schermo intero */}
      <SensorMap
        sensors={sensors}
        displacements={displacements}
        showArrows={showArrows}
        showAttribution={false}
        height="100%"
      />

      {/* Toolbar sovrapposta in alto a sinistra */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: isMobile ? 56 : 12,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 1,
          zIndex: 1000,
        }}
      >
        <Paper sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
          <Tooltip title="Aggiorna">
            <IconButton size="small" onClick={loadData}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={showArrows ? 'Nascondi frecce' : 'Mostra frecce'}>
            <IconButton size="small" onClick={() => setShowArrows(!showArrows)}>
              {showArrows ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Chip
            size="small"
            label={wsConnected ? 'LIVE' : 'OFF'}
            color={wsConnected ? 'success' : 'default'}
            sx={{ height: 24, fontSize: 10 }}
          />
        </Paper>

        {/* Stats rapide - nascoste su mobile per spazio */}
        {!isMobile && (
          <Paper sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.5 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>Sensori</Typography>
              <Typography variant="body2" fontWeight={600} color="primary">{summary?.online || 0}/{summary?.total || 0}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>Allarmi</Typography>
              <Typography variant="body2" fontWeight={600} color="error.main">{alarms.length}</Typography>
            </Box>
          </Paper>
        )}
      </Box>

      {/* FAB per aprire pannello */}
      <Fab
        color="primary"
        size={isMobile ? 'small' : 'medium'}
        onClick={() => setPanelOpen(true)}
        sx={{
          position: 'absolute',
          right: isMobile ? 12 : 16,
          bottom: isMobile ? 24 : 'auto',
          top: isMobile ? 'auto' : '50%',
          transform: isMobile ? 'none' : 'translateY(-50%)',
          zIndex: 1000,
          display: panelOpen ? 'none' : 'flex',
        }}
      >
        <Badge badgeContent={displacements.length + alarms.length} color="error" max={99}>
          <TimelineIcon />
        </Badge>
      </Fab>

      {/* Pannello spostamenti/allarmi - bottom su mobile, right su desktop */}
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        variant={isMobile ? 'temporary' : 'persistent'}
        sx={{
          '& .MuiDrawer-paper': isMobile
            ? {
                height: '65vh',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              }
            : {
                width: PANEL_WIDTH,
                boxSizing: 'border-box',
                top: 0,
                height: '100%',
              },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header pannello */}
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
            <IconButton size="small" onClick={() => setPanelOpen(false)}>
              <ChevronRightIcon />
            </IconButton>
            <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
              <Chip
                icon={<TimelineIcon />}
                label={`Spostamenti (${displacements.length})`}
                onClick={() => setPanelTab('displacements')}
                color={panelTab === 'displacements' ? 'primary' : 'default'}
                size="small"
              />
              <Chip
                icon={<WarningIcon />}
                label={`Allarmi (${alarms.length})`}
                onClick={() => setPanelTab('alarms')}
                color={panelTab === 'alarms' ? 'error' : 'default'}
                size="small"
              />
            </Box>
          </Box>

          {/* Contenuto pannello */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {panelTab === 'displacements' && (
              <>
                {displacements.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <TimelineIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2">Nessuno spostamento</Typography>
                    <Typography variant="caption">Imposta una posizione di riferimento nelle Impostazioni</Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {displacements.map((d) => {
                      const sensor = sensors.find((s) => s.id === d.sensor_id);
                      return (
                        <Paper key={d.sensor_id} sx={{ mb: 1, p: 1.5 }} variant="outlined">
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: sensor?.status?.toLowerCase() === 'online' ? '#4caf50' :
                                    sensor?.status?.toLowerCase() === 'warning' ? '#ff9800' :
                                    sensor?.status?.toLowerCase() === 'alarm' ? '#f44336' : '#9e9e9e',
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="subtitle2" fontWeight={600}>
                                {sensor?.name || d.sensor_id}
                              </Typography>
                            </Box>
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ExploreIcon sx={{ fontSize: 14, color: '#4caf50', transform: 'rotate(-45deg)' }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">N</Typography>
                                  <Typography variant="body2" fontWeight={500}>{formatMM(d.delta_north)}</Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ExploreIcon sx={{ fontSize: 14, color: '#2196f3', transform: 'rotate(45deg)' }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">E</Typography>
                                  <Typography variant="body2" fontWeight={500}>{formatMM(d.delta_east)}</Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <HeightIcon sx={{ fontSize: 14, color: '#ff9800' }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">U</Typography>
                                  <Typography variant="body2" fontWeight={500}>{formatMM(d.delta_up)}</Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <SpeedIcon sx={{ fontSize: 14, color: '#9c27b0' }} />
                              <Typography variant="body2" fontWeight={600} color="primary">
                                {formatMM(d.total || d.total_displacement)} mm
                              </Typography>
                            </Box>
                            {d.azimuth !== undefined && (
                              <Typography variant="caption" color="text.secondary">
                                {d.azimuth?.toFixed(0)}° N
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      );
                    })}
                  </List>
                )}
              </>
            )}

            {panelTab === 'alarms' && (
              <AlarmPanel
                alarms={alarms}
                onAcknowledge={handleAcknowledge}
                onResolve={handleResolve}
              />
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default Dashboard;
